import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next'
import CustomCard from "../../components/CustomCard";
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import moment from 'moment'
import 'moment/locale/tr'
import { DEFAULT_LANGUAGE, ROUTES } from '../../util/constants';
import { formatAddress } from '../../util/helper'

// MUI
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography'

// Redux Actions
import { showError, showSuccess } from '../../redux/snackbar'

// Custom
import CustomLinearProgress from '../../components/CustomLinearProgress'

// Api Requests
import { getAppointmentDetail, cancelAppointment } from '../../services/index'

// Initialize moment
moment.locale(DEFAULT_LANGUAGE)

function withRouter(Component) {
    function ComponentWithRouterProp(props) {
        let location = useLocation();
        let navigate = useNavigate();
        let params = useParams();
        return (
            <Component
                {...props}
                router={{ location, navigate, params }}
            />
        );
    }

    return ComponentWithRouterProp;
}

const paddingRoot = { xs: 2, md: 2, lg: 3, xl: 4 }

const Index = (props) => {

    const { t } = useTranslation()
    const { dispatch } = props
    const { id } = useParams();

    const [loading, setLoading] = useState(false);
    const [appointmentStatus, setAppointmentsStatus] = useState("");
    const [appointmentCanceled, setAppointmentCanceled] = useState(false);
    const [appointmentObj, setAppointmentObj] = useState([]);



    // Theme
    const theme = useTheme();


    const styles = {
        contentRoot: {
            [theme.breakpoints.down('md')]: {
                flexDirection: 'column',
                flexWrap: 'nowrap',
            },
            flexDirection: 'row',
        },
        rightColumnRoot: {
            [theme.breakpoints.down('md')]: {
                flex: '1 0 0',
            },
            textAlign: "center",
        },

        cardBox: {
            marginTop: "20px"
        }
    }
    const smDown = useMediaQuery(theme.breakpoints.down('md'));

    // Spacing
    const spacingRoot = { xs: 2, sm: 2, md: 2, lg: 3, xl: 4 }

    // On mount
    useEffect(() => {
        setLoading(true)
        // Get appointment
        getAppointmentDetail(id)
            .then(_appointment => {

                setAppointmentObj(_appointment)
                setAppointmentsStatus(_appointment.appointmentStatus?.key ?? "")
                isAppointmentCancelled(_appointment)

            })
            .catch(e => {
                console.error(e)
                dispatch(showError(t('UNKNOWN_ERROR')))

            }).finally(() => setLoading(false))


    }, [])

    function handleCancelAppointment() {
        setLoading(true)

        // Cancel appointment
        cancelAppointment(id)
            .then(isCanceled => {
                if (isCanceled) {
                    setAppointmentCanceled(true);
                    dispatch(showSuccess(t('appointmentCanceled')))
                }
                else {
                    dispatch(showError(t('UNKNOWN_ERROR')))
                }

            })
            .catch(e => {
                console.error(e)
                dispatch(showError(t('UNKNOWN_ERROR')))
            }).finally(() => setLoading(false))

    }

    function isAppointmentCancelled(appointment) {
        if (appointment.appointmentStatus.key === "canceled_by_impetus" || appointment.appointmentStatus.key === "canceled_by_customer" || appointment.appointmentStatus.key === "canceled_by_system") {
            setAppointmentCanceled(true);
        } else {
            setAppointmentCanceled(false);
        }
    }

    function getContent() {
        return !appointmentCanceled ?
            <Grid container direction="column" wrap='nowrap' alignItems="stretch">

                {/* Content */}
                <Grid item container style={{ flex: '1 0 0' }} sx={styles.contentRoot}>

                    {/* Form */}
                    <Grid item xs={12} md={12} sx={styles.rightColumnRoot}>

                        {/* Title */}
                        <Grid item container xs={12} sm={12} md={12} direction="row" alignItems="flex-start" justifyContent="center">

                            <Typography variant='h4' color='textPrimary'>
                                {t('appointmentDetails')}
                            </Typography>
                        </Grid>

                        <Box width={1} sx={styles.cardBox}>

                            {/* Title */}
                            <Grid item xs={12}>
                                <Typography variant='subtitle2' color='textPrimary'>
                                    {t('date')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} justifyContent="center" container wrap='nowrap'>
                                {
                                    appointmentObj.appointmentDate && appointmentObj.timeSlot ?
                                        <Typography variant='body1' color='textSecondary'>{moment(appointmentObj.appointmentDate + ' ' + appointmentObj.timeSlot).format('Do MMMM YYYY, HH:mm')}</Typography>
                                        : ""
                                }
                            </Grid>

                        </Box>

                        <Box width={1} sx={styles.cardBox}>

                            {/* Title */}
                            <Grid item xs={12}>

                                <Typography variant='subtitle2' color='textPrimary'>
                                    {t('dealer')}
                                </Typography>

                            </Grid>

                            <Grid item xs={12} justifyContent="center" wrap='nowrap'>

                                <Typography variant='body1' color='textSecondary'>{formatAddress(appointmentObj?.dealer?.name ?? "")}</Typography>
                                <Typography variant='body1' color='textSecondary'>{formatAddress(appointmentObj?.dealer?.address ?? "")}</Typography>

                            </Grid>

                            <Grid item xs={12} justifyContent="center" container wrap='nowrap'>

                                <Typography variant='body1' color='textSecondary'>{formatAddress(appointmentObj?.dealer?.phone1 ?? "")}</Typography>

                            </Grid>

                        </Box>

                        <Box width={1} sx={styles.cardBox}>

                            <Grid item xs={12}>
                                <Typography variant='subtitle2' color='textPrimary'>
                                    {t('vehicleInfo')}
                                </Typography>
                            </Grid>

                            <Grid justifyContent="center" container wrap='nowrap'>
                                <Typography variant='body1' color='textSecondary'>{appointmentObj?.vehicle?.modelName ?? ""}</Typography>
                            </Grid>

                            <Grid item xs={12} justifyContent="center" container wrap='nowrap'>
                                <Typography variant='body1' color='textSecondary'>{appointmentObj?.vehicle?.plate ?? ""}</Typography>
                            </Grid>

                        </Box>

                        <Box width={1} sx={styles.cardBox}>

                            {/* Title */}
                            <Grid item xs={12}>
                                <Typography variant='subtitle2' color='textPrimary'>
                                    {t('serviceRequest')}
                                </Typography>
                            </Grid>

                            {
                                appointmentObj?.appointmentNotes ?
                                    appointmentObj.appointmentNotes.map(a => (
                                        <Grid item xs={12} justifyContent="center" container wrap='nowrap'>
                                            <Typography variant='body1' color='textSecondary'>{a.note}</Typography>
                                        </Grid>
                                    ))
                                    :
                                    null
                            }

                        </Box>

                    </Grid>

                </Grid>
            </Grid>
            : getCancelCard();
    }

    function getCancelCard() {
        return (
            <Grid container direction="column" wrap='nowrap' alignItems="stretch">
                {/* Content */}
                <Grid item container style={{ flex: '1 0 0', }} sx={styles.contentRoot}>

                    <Grid item container xs={12} sm={12} md={12} direction="row" justifyContent="center" style={{ textAlign: "center" }}>

                        <Typography variant='h4' color='textPrimary'>
                            {t('appointmentCanceled')}
                        </Typography>

                        <Box width={1} sx={styles.cardBox}>

                            {/* Title */}
                            <Grid item xs={12}>
                                <Typography variant='subtitle2' color='textPrimary'>
                                    {t('date')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} justifyContent="center" container wrap='nowrap'>
                                {
                                    appointmentObj.appointmentDate && appointmentObj.timeSlot ?
                                        <Typography variant='body1' color='textSecondary'>{moment(appointmentObj.appointmentDate + ' ' + appointmentObj.timeSlot).format('Do MMMM YYYY, HH:mm')}</Typography>
                                        : ""
                                }
                            </Grid>

                        </Box>

                        <Box width={1} sx={styles.cardBox}>

                            {/* Title */}
                            <Grid item xs={12}>
                                <Typography variant='subtitle2' color='textPrimary'>
                                    {t('dealer')}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} justifyContent="center" wrap='nowrap'>
                                <Typography variant='body1' color='textSecondary'>{formatAddress(appointmentObj.dealer.name)}</Typography>
                                <Typography variant='body1' color='textSecondary'>{formatAddress(appointmentObj.dealer.address)}</Typography>
                            </Grid>

                            <Grid item xs={12} justifyContent="center" container wrap='nowrap'>
                                <Typography variant='body1' color='textSecondary'>{formatAddress(appointmentObj.dealer.phone1)}</Typography>
                            </Grid>
                        </Box>

                    </Grid>

                </Grid>
            </Grid>
        );
    }

    function getCardActions() {
        return (
            <Box display='flex' justifyContent={smDown ? 'space-around' : 'space-between'}>
                <Button variant="contained" style={{ minWidth: smDown ? '50%' : "150px" }} onClick={handleCancelAppointment}>
                    {t('cancelAppointment')}
                </Button>

                <Box m={1} />
                <Button
                    variant="contained"
                    color="primary"
                    sx={styles.button}
                    style={{ minWidth: smDown ? '50%' : '150px' }}
                    onClick={() => window.open("https://www.google.com/maps/search/?api=1&query=" + appointmentObj.dealer.latitude + "," + appointmentObj.dealer.longitude, "_blank")}
                >
                    {t('getRoute')}
                </Button>
            </Box>
        )
    }

    function getHomepageAction() {
        return (
            <Box display='flex' justifyContent={smDown ? 'space-around' : 'center'}>
                <Button
                    variant="contained"
                    color="primary"
                    sx={styles.button}
                    style={{ minWidth: smDown ? '50%' : '150px' }}
                    onClick={() => props.history.push(ROUTES.INDEX)}
                >
                    {t('goToHomepage')}
                </Button>
            </Box>
        )
    }

    return (
        <Box width={1} height={1} p={paddingRoot} display='flex' bgcolor="background.default" overflow='hidden'>

            <CustomLinearProgress loading={loading} />

            <CustomCard
                padding={spacingRoot}
                content={getContent()}
                action={appointmentStatus === "requested" && !appointmentCanceled ? getCardActions() : getHomepageAction()}
            />
        </Box>
    );

}

export default withRouter(Index)